import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePromptRegistry } from "@/queries";
import { keepRelevantSearchParams } from "@/utils/utils";
import { StarIcon as StarIconSolid } from "@heroicons/react/solid";
import { Folder } from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SidebarCard from "./SidebarSearchLayout/SidebarCard";
import { OverflowDisplayTooltip } from "./ui/Tooltip/OverflowDisplayTooltip";

export const SidebarRequestLogCard = ({
  active,
  logId,
  descriptor,
  tags,
  startTime,
  starred,
  prompt,
  response,
  responseIsFunction,
  type,
  cardinality,
  promptId,
  customModelName,
}: {
  active: boolean;
  logId: number;
  descriptor: string;
  tags: string[];
  startTime: string;
  starred: boolean;
  prompt: string;
  response: string;
  responseIsFunction?: boolean;
  type: string | "request";
  cardinality: number | undefined;
  promptId?: number;
  customModelName?: string | null;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userContext = useUser();
  const auth = useAuth();
  // TODO (naveed) LOW: try to get prompt_names in higher component instead of making network call for each card.
  const promptRegistryQuery = usePromptRegistry(
    auth?.userToken!,
    promptId,
    false,
  );
  const prompt_name = promptRegistryQuery.data?.prompt_name;

  // TODO: (naveed) LOW: moment.utc(startTime).local().fromNow() is fairly heavy if we do it for 100 cards
  // we could pre‑compute the formatted string and pass it as a prop.
  let timestamp = moment.utc(startTime).local().fromNow();
  if (moment.utc().isSame(moment.utc(startTime).local(), "day")) {
    timestamp = moment.utc(startTime).local().format("h:mm:ss A");
  }

  const [tagsList, setTagsList] = useState<string[]>(tags || []);

  // TODO (naveed) LOW: remove useEffect and useState completely and we can use the prop directly.
  useEffect(() => {
    setTagsList(tags || []);
  }, [tags]);

  return (
    <SidebarCard
      active={active}
      type={type}
      id={String(logId)}
      onClick={() => {
        // TODO: (naveed) LOW: Every render we create a brand‑new onClick arrow function, which invalidates SidebarCard’s props and forces it to re‑render.
        // Wrap it in useCallback (or, even better, move navigation logic into SidebarCard itself via context or a logId prop):
        // However I would love to keep it as a Link component instead of onclick card which makes the user more feasible to open the request in new tab by cmd + click.
        const preservedParams = keepRelevantSearchParams(searchParams);
        let url = "";

        if (type === "group") {
          url = `/workspace/${userContext?.activeWorkspaceId}/group/${logId}?${preservedParams}`;
        } else {
          url = `/workspace/${userContext?.activeWorkspaceId}/request/${logId}?${preservedParams}`;
        }

        navigate(url);
      }}
    >
      <div className="flex max-w-[200px] items-center gap-2 overflow-hidden">
        {starred ? (
          <StarIconSolid
            className="h-4 w-auto text-yellow-500"
            aria-hidden="true"
          />
        ) : null}
        <div
          className={`w-full truncate font-light ${
            active ? "text-gray-700" : "text-gray-600"
          }`}
        >
          <OverflowDisplayTooltip
            content={customModelName || descriptor}
            className="block truncate"
          >
            {customModelName || descriptor}
          </OverflowDisplayTooltip>
        </div>
      </div>
      <div>
        <div
          className={`line-clamp-2 ${
            active ? "text-gray-600" : "text-gray-500"
          }`}
          title={prompt}
        >
          {type !== "group" ? (
            <span className="text-xs font-medium text-gray-700">Prompt: </span>
          ) : (
            <span className="text-xs font-light text-gray-700">Group </span>
          )}
          {type === "group" && (
            <span
              className={`${active ? "font-normal" : "font-light"}`}
            >{`[${logId}]`}</span>
          )}
          {type !== "group" && (
            <span className={`${active ? "font-normal" : "font-light"}`}>
              {typeof prompt === "string" ? prompt : ""}
            </span>
          )}
        </div>
        <div
          className={`line-clamp-2 ${
            active ? "text-gray-600" : "text-gray-500"
          } mt-1`}
          title={response}
        >
          <span className="text-xs font-medium text-gray-700">
            {type !== "group" ? (
              <span className="text-xs font-medium text-gray-700">
                Response:{" "}
              </span>
            ) : (
              <span className="text-xs font-medium text-gray-700">Size: </span>
            )}
          </span>
          {type !== "group" ? (
            <span
              className={`${active ? "font-normal" : "font-light"} ${
                responseIsFunction && "font-mono"
              }`}
            >
              {typeof response === "string" ? response : ""}
              {responseIsFunction && "()"}
            </span>
          ) : (
            <span className={`${active ? "font-normal" : "font-light"}`}>
              {cardinality} {cardinality === 1 ? "request" : "requests"}
            </span>
          )}
        </div>
      </div>
      <div className="space-y-1">
        {tagsList?.map((tag: string) => (
          <div
            key={tag}
            className={`mr-1 inline-flex items-center rounded-sm px-1.5 py-0.5 font-medium text-gray-400 ${
              active ? "bg-blue-100 text-gray-600" : "bg-gray-100"
            }`}
          >
            {tag}
          </div>
        ))}
      </div>
      <div
        className={`flex gap-x-4 ${
          prompt_name ? "justify-between" : "justify-end"
        }`}
      >
        {prompt_name === null || !prompt_name ? null : (
          <div
            className={`inline-flex items-center gap-1 ${
              active ? "text-gray-500" : "text-gray-400"
            } flex-grow truncate font-light`}
          >
            <Folder className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{prompt_name}</span>
          </div>
        )}
        <div
          title={startTime}
          className={`whitespace-nowrap ${
            active ? "text-gray-500" : "text-gray-400"
          } font-light`}
        >
          {timestamp}
        </div>
      </div>
    </SidebarCard>
  );
};
